import { HttpClient, HttpContext, HttpErrorResponse, httpResource } from '@angular/common/http';
import { Service, computed, inject, signal } from '@angular/core';
import { Observable, map } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { ResponseApi } from '../../../core/dtos/response-api';
import { HANDLE_HTTP_ERROR_LOCALLY, SKIP_SUCCESS_ALERT } from '../../../core/http/http-context-tokens';
import {
  CABECALHO_DESAFIO,
  CAMPO_HONEYPOT,
  MENSAGEM_DESAFIO_RECUSADO,
  ehRecusaDoDesafio,
} from './desafio-formulario.service';
import {
  MENSAGEM_CAPTCHA_RECUSADO,
  RespostaCaptcha,
  cabecalhosCaptcha,
  ehRecusaDoCaptcha,
} from './captcha-consulta.service';
import {
  AgendaPublicaResponse,
  AgendamentoEscolhido,
  ErroSolicitacaoServico,
  formatarDataPrazo,
  FormularioServicoPublicoResponse,
  PendenciaPublicaResponse,
  RespostaPublicaResponse,
  SolicitacaoAberturaResponse,
  SolicitacaoPublicaResponse,
  statusParaAcompanhamento,
  textoPrazoSolicitacao,
} from '../models/solicitacao-servico.model';
import {
  EventoHistorico,
  PendenciaSolicitacao,
  RespostaSolicitacao,
  Solicitacao,
  TomHistorico,
} from './solicitacoes.service';

const RECURSO = `${environment.apiUrl}/sai/cartaservico/publico`;

/** O erro fica local: cada etapa decide o que mostrar, sem o modal do interceptor. */
const ERRO_LOCAL = () => new HttpContext().set(HANDLE_HTTP_ERROR_LOCALLY, true);

/** As duas frases de 409 do back-end — é por elas que a tela sabe para onde voltar. */
export const MENSAGEM_FORMULARIO_DESATUALIZADO =
  'O formulário deste serviço foi atualizado. Recarregue a página e preencha novamente.';

const LIMITE = 'Muitas tentativas em pouco tempo. Aguarde alguns instantes e tente novamente.';
const INDISPONIVEL = 'Não foi possível registrar a solicitação agora. Tente novamente em instantes.';
const NAO_ENCONTRADO = 'Não encontramos solicitação com este protocolo.';
const SESSAO_EXPIRADA =
  'Sua sessão expirou. Volte à etapa Informações e entre novamente com o seu login para enviar — ' +
  'os dados preenchidos foram mantidos.';

/**
 * A superfície pública da **solicitação de serviço** vista pelo portal: o formulário do
 * serviço, a agenda, a abertura, a consulta por protocolo e o download dos anexos da resposta.
 *
 * A abertura é anônima ou identificada. Na identificada o portal só DECLARA a escolha
 * (`BitAnonima=false`): quem é o solicitante o back-end lê do token que o `JwtInterceptor`
 * já anexa — nome, CPF e contato nunca vão no `FormData`.
 *
 * ⚠️ O portal **não sabe nem envia o órgão**: o back-end o resolve pelo host de quem chamou.
 * O `FormData` da abertura nunca escreve `codOrgaoOrg`, `protocolo` nem `status` — lá são
 * campos do sistema, e mandá-los devolve 400.
 *
 * O formulário é carregado só quando o cidadão entra na solicitação (`definirServico`), e não
 * junto da carta: a carta é lida por todo visitante; o formulário, só por quem pede o serviço.
 */
@Service()
export class SolicitacaoServicoPublicaService {
  private readonly http = inject(HttpClient);

  /** Nulo até o cidadão abrir a solicitação — e então nenhuma request é feita. */
  private readonly codServico = signal<number | null>(null);

  private readonly formularioApi = httpResource<ResponseApi<FormularioServicoPublicoResponse>>(() => {
    const cod = this.codServico();

    if (cod === null) return undefined;

    return { url: `${RECURSO}/servico/${cod}/formulario`, context: ERRO_LOCAL() };
  });

  readonly carregandoFormulario = computed(() => this.formularioApi.isLoading());

  readonly falhouFormulario = computed(() => this.formularioApi.error() !== undefined);

  /**
   * O formulário, ou `null` enquanto não pedido, no ar ou em falha. O `error()` vem ANTES do
   * `value()`: em erro o `value()` lança, e a tela que mostra a falha lê este signal.
   */
  readonly formulario = computed<FormularioServicoPublicoResponse | null>(() => {
    if (this.formularioApi.error() !== undefined) return null;

    return this.formularioApi.value()?.data ?? null;
  });

  definirServico(codServicoSrv: number | null): void {
    this.codServico.set(codServicoSrv);
  }

  /** "Tentar novamente" da falha — e a recarga depois de um 409 de formulário desatualizado. */
  recarregarFormulario(): void {
    this.formularioApi.reload();
  }

  /**
   * Os horários livres de um mês (`aaaa-mm`), num posto do serviço.
   *
   * `posto` só vai na URL com o código de um endereço **adicional**. Sem código — o endereço
   * principal, que é o da página do serviço — o parâmetro não é enviado: é a chamada de sempre.
   */
  agenda(
    codServicoSrv: number,
    mes: string,
    codLocalAtendimentoServicoLas?: number | null,
  ): Observable<AgendaPublicaResponse> {
    const params: Record<string, string | number> = { mes };

    if (typeof codLocalAtendimentoServicoLas === 'number') params['posto'] = codLocalAtendimentoServicoLas;

    return this.http
      .get<ResponseApi<AgendaPublicaResponse>>(`${RECURSO}/servico/${codServicoSrv}/agenda`, {
        params,
        context: ERRO_LOCAL(),
      })
      .pipe(map((resposta) => resposta.data));
  }

  /**
   * Abre a solicitação. O alerta automático de sucesso é silenciado: o recibo na Confirmação
   * **é** a resposta ao cidadão. Sem `Content-Type` manual — quem põe o boundary é o navegador.
   *
   * O `desafio` de formulário vai no cabeçalho `X-Desafio-Formulario`, no anônimo e no
   * identificado (change anti-abuso-superficie-publica).
   */
  abrir(corpo: FormData, desafio: string): Observable<SolicitacaoAberturaResponse> {
    return this.http
      .post<ResponseApi<SolicitacaoAberturaResponse>>(`${RECURSO}/solicitacao`, corpo, {
        context: ERRO_LOCAL().set(SKIP_SUCCESS_ALERT, true),
        headers: { [CABECALHO_DESAFIO]: desafio },
      })
      .pipe(map((resposta) => resposta.data));
  }

  /**
   * A consulta pública pelo protocolo inteiro, com o captcha da tela nos cabeçalhos (change
   * captcha-consulta-protocolo). O código vale uma tentativa.
   */
  consultar(protocolo: string, captcha: RespostaCaptcha): Observable<SolicitacaoPublicaResponse> {
    return this.http
      .get<ResponseApi<SolicitacaoPublicaResponse>>(rotaSolicitacao(protocolo), {
        context: ERRO_LOCAL(),
        headers: cabecalhosCaptcha(captcha),
      })
      .pipe(map((resposta) => resposta.data));
  }

  /**
   * O arquivo de um anexo da **resposta do órgão**, em `blob` — fora do envelope `ResponseApi`.
   *
   * O protocolo continua sendo a credencial: o back-end só entrega o anexo que for de uma
   * resposta DESTE protocolo, no órgão do host, e devolve o mesmo 404 para qualquer
   * desencontro. O erro fica local para a tela avisar junto do arquivo, sem o modal global.
   */
  baixarAnexoResposta(protocolo: string, codAnexo: number): Observable<Blob> {
    return this.http.get(`${rotaSolicitacao(protocolo)}/resposta/anexo/${codAnexo}`, {
      responseType: 'blob',
      context: ERRO_LOCAL(),
    });
  }

  /**
   * Envia o complemento que o órgão pediu, pelo protocolo — a mesma credencial da consulta. A
   * resposta é a consulta pública já atualizada. O erro fica no card e o sucesso é a própria
   * tela, então os dois alertas automáticos são silenciados.
   */
  enviarComplemento(protocolo: string, texto: string, arquivos: readonly File[]): Observable<SolicitacaoPublicaResponse> {
    return this.http
      .post<ResponseApi<SolicitacaoPublicaResponse>>(
        `${rotaSolicitacao(protocolo)}/complemento`,
        montarFormDataComplemento(texto, arquivos),
        { context: ERRO_LOCAL().set(SKIP_SUCCESS_ALERT, true) },
      )
      .pipe(map((resposta) => resposta.data));
  }
}

/** O `multipart/form-data` do complemento: o texto aparado e um `Anexos` por arquivo, na ordem. */
export function montarFormDataComplemento(texto: string, arquivos: readonly File[]): FormData {
  const corpo = new FormData();

  corpo.append('DesTexto', texto.trim());

  for (const arquivo of arquivos) {
    corpo.append('Anexos', arquivo, arquivo.name);
  }

  return corpo;
}

/** O que o card do complemento mostra quando o envio falha. */
export type ErroComplemento = Pick<ErroSolicitacaoServico, 'mensagem' | 'detalhes'>;

/**
 * Traduz o erro do envio do complemento. Como na abertura, a `message` da API vence: o 409 e o
 * 422 dizem ao cidadão o que aconteceu com a solicitação enquanto ele escrevia.
 */
export function mapearErroComplemento(erro: unknown): ErroComplemento {
  const resposta = erro instanceof HttpErrorResponse ? erro : null;
  const corpo = (resposta?.error ?? null) as Partial<ResponseApi<unknown>> | null;

  const mensagemApi = typeof corpo?.message === 'string' ? corpo.message.trim() : '';

  const detalhes = (Array.isArray(corpo?.errors) ? corpo.errors : [])
    .filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
    .map((item) => item.trim())
    .filter((item) => item !== mensagemApi);

  switch (resposta?.status) {
    case 400:
      return { mensagem: mensagemApi || 'Não foi possível enviar o complemento com estes dados.', detalhes };

    case 404:
      return { mensagem: mensagemApi || NAO_ENCONTRADO, detalhes: [] };

    case 409:
      return {
        mensagem:
          mensagemApi ||
          'A solicitação foi alterada pelo órgão enquanto você enviava o complemento. Consulte o protocolo de novo.',
        detalhes: [],
      };

    case 413:
      return {
        mensagem: 'Os arquivos passam do tamanho total aceito. Remova algum anexo e tente de novo.',
        detalhes: [],
      };

    case 422:
      return { mensagem: mensagemApi || 'Esta solicitação não está aguardando complemento.', detalhes: [] };

    case 429:
      return { mensagem: LIMITE, detalhes: [] };

    default:
      return {
        mensagem: 'Não foi possível enviar o complemento agora. Tente novamente em instantes.',
        detalhes: [],
      };
  }
}

/** A rota da solicitação pelo protocolo — aparado, em maiúsculas e codificado, como o back-end o compara. */
function rotaSolicitacao(protocolo: string): string {
  return `${RECURSO}/solicitacao/${encodeURIComponent(protocolo.trim().toUpperCase())}`;
}

/** O que o `FormData` da abertura carrega. Não há campo do sistema aqui — por construção. */
export interface DadosAberturaSolicitacao {
  codServicoSrv: number;
  /** A escolha do cidadão. `false` exige a sessão do login — sem ela o back-end devolve 401. */
  anonima: boolean;
  /** "Segunda via" no tipo do pedido; decide o prazo aplicado. */
  segundaVia: boolean;
  endereco: string;
  numero: string;
  complemento: string;
  cep: string;
  bairro: string;
  cidade: string;
  uf: string;
  descricao: string;
  observacoes: string;
  agendamento: AgendamentoEscolhido | null;
  /** Respostas por código de campo, na ordem do formulário. */
  campos: readonly { codCampoServicoCps: number; valor: string; arquivo: File | null }[];
  anexos: readonly File[];
  /** O campo invisível do anti-abuso. Vazio para toda pessoa — e então não vai. */
  honeypot?: string;
}

/**
 * Monta o `multipart/form-data` da abertura.
 *
 * Função pura e exportada: é o ponto em que o contrato do back-end é honrado, e isso merece
 * teste sem HTTP.
 *
 * 1. **só chave com valor** — string vazia é ruído no contrato;
 * 2. campos específicos em `Campos[i].CodCampoServicoCps` / `.Valor` / `.Arquivo`, que é como o
 *    binder do ASP.NET monta a lista; só entra campo com valor ou arquivo;
 * 3. data e horário como **texto** (`aaaa-mm-dd` e `HH:mm`) — o back-end os lê com formato exato;
 * 4. um `Anexos` por arquivo, na ordem em que o cidadão os adicionou;
 * 5. `BitAnonima` SEMPRE explícito: com sessão ativa o token vai junto mesmo na anônima, e é
 *    este campo que impede o back-end de tomá-la por identificada (o padrão dele é anônima).
 */
export function montarFormDataSolicitacao(dados: DadosAberturaSolicitacao): FormData {
  const corpo = new FormData();

  const texto = (chave: string, valor: string | null | undefined): void => {
    const limpo = (valor ?? '').trim();

    if (limpo) corpo.append(chave, limpo);
  };

  corpo.append('CodServicoSrv', String(dados.codServicoSrv));
  corpo.append('BitAnonima', dados.anonima ? 'true' : 'false');
  corpo.append('BitSegundaVia', dados.segundaVia ? 'true' : 'false');

  texto('DesEndereco', dados.endereco);
  texto('DesNumero', dados.numero);
  texto('DesComplemento', dados.complemento);
  texto('DesCep', dados.cep);
  texto('DesBairro', dados.bairro);
  texto('DesCidade', dados.cidade);
  texto('DesUf', dados.uf);
  texto('DesDescricao', dados.descricao);
  texto('DesObservacoes', dados.observacoes);

  if (dados.agendamento) {
    texto('DataAgendamento', dados.agendamento.data);
    texto('HorarioAgendamento', dados.agendamento.horario);
  }

  let indice = 0;

  for (const campo of dados.campos) {
    const valor = campo.valor.trim();

    if (!valor && !campo.arquivo) continue;

    corpo.append(`Campos[${indice}].CodCampoServicoCps`, String(campo.codCampoServicoCps));

    if (valor) corpo.append(`Campos[${indice}].Valor`, valor);
    if (campo.arquivo) corpo.append(`Campos[${indice}].Arquivo`, campo.arquivo, campo.arquivo.name);

    indice++;
  }

  for (const arquivo of dados.anexos) {
    corpo.append('Anexos', arquivo, arquivo.name);
  }

  // O honeypot segue a regra 1: só preenchido — e preenchido, a API recusa o envio.
  texto(CAMPO_HONEYPOT, dados.honeypot);

  return corpo;
}

/**
 * Traduz o erro HTTP para o que a tela decide. A `message` da API é preferida sempre que
 * existir: a regra recusada é do serviço, e reescrevê-la esconderia o motivo real.
 */
export function mapearErroSolicitacao(
  erro: unknown,
  contexto: 'abertura' | 'consulta' = 'abertura',
): ErroSolicitacaoServico {
  const resposta = erro instanceof HttpErrorResponse ? erro : null;
  const corpo = (resposta?.error ?? null) as Partial<ResponseApi<unknown>> | null;

  const mensagemApi = typeof corpo?.message === 'string' ? corpo.message.trim() : '';

  // A mensagem costuma repetir a única violação; sem o filtro ela apareceria duas vezes.
  const detalhes = (Array.isArray(corpo?.errors) ? corpo.errors : [])
    .filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
    .map((item) => item.trim())
    .filter((item) => item !== mensagemApi);

  // O 422 do desafio de formulário — pelo cabeçalho da API, nunca pelo texto. O portal já pediu
  // outro desafio: o cidadão só envia de novo.
  if (contexto === 'abertura' && ehRecusaDoDesafio(erro)) {
    return { tipo: 'desafio', mensagem: MENSAGEM_DESAFIO_RECUSADO, detalhes: [] };
  }

  // O 422 do captcha da consulta, também pelo cabeçalho: o código foi consumido e a tela traz outro.
  if (contexto === 'consulta' && ehRecusaDoCaptcha(erro)) {
    return { tipo: 'captcha', mensagem: MENSAGEM_CAPTCHA_RECUSADO, detalhes: [] };
  }

  switch (resposta?.status) {
    case 400:
      return {
        tipo: 'validacao',
        mensagem: mensagemApi || 'Não foi possível registrar a solicitação com estes dados.',
        detalhes,
      };

    case 401:
      // Só a identificada chega aqui: o token venceu ou sumiu. O interceptor já encerrou a sessão.
      return { tipo: 'sessao', mensagem: SESSAO_EXPIRADA, detalhes: [] };

    case 403:
      return {
        tipo: 'identificacao',
        mensagem: mensagemApi || 'Este serviço exige identificação do cidadão.',
        detalhes: [],
      };

    case 404:
      return contexto === 'consulta'
        ? { tipo: 'nao-encontrado', mensagem: mensagemApi || NAO_ENCONTRADO, detalhes: [] }
        : {
            tipo: 'nao-encontrado',
            mensagem: 'Este serviço não está disponível para solicitação neste portal.',
            detalhes: [],
          };

    case 409:
      return mensagemApi === MENSAGEM_FORMULARIO_DESATUALIZADO
        ? { tipo: 'formulario', mensagem: mensagemApi, detalhes: [] }
        : {
            tipo: 'vaga',
            mensagem: mensagemApi || 'Este horário acabou de ser preenchido. Escolha outro horário.',
            detalhes: [],
          };

    case 413:
      return {
        tipo: 'validacao',
        mensagem: 'Os arquivos passam do tamanho total aceito. Remova algum anexo e tente de novo.',
        detalhes: [],
      };

    case 429:
      // O 429 vem em texto puro, fora do envelope: é do pipeline, antes do handler.
      return { tipo: 'limite', mensagem: LIMITE, detalhes: [] };

    default:
      // Status 0 (rede/CORS) e 5xx: o cidadão não tem o que corrigir, só tentar de novo.
      return { tipo: 'indisponivel', mensagem: INDISPONIVEL, detalhes: [] };
  }
}

const TOM_POR_STATUS: Readonly<Record<string, TomHistorico>> = {
  Recebida: 'azul',
  Respondida: 'verde',
  Concluída: 'verde',
  Cancelada: 'vermelho',
};

/**
 * A cor do evento na linha do tempo, pelo status em que a solicitação ficou. Exportada porque o
 * agendamento do cidadão é uma solicitação de serviço: "Meus agendamentos" pinta o histórico
 * dele com a mesma regra do acompanhamento por protocolo.
 */
export function tomDoStatusSolicitacao(status: string): TomHistorico {
  return TOM_POR_STATUS[status] ?? 'ambar';
}

/** `2048` → `2.0 KB`: o tamanho dos anexos no acompanhamento e em "Minhas solicitações". */
export function tamanhoLegivel(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;

  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** `2026-09-24T10:30:00` → `24/09/2026 - 10:30`, o formato do histórico da tela. */
export function dataHoraHistorico(iso: string): string {
  const [data, hora = ''] = iso.split('T');
  const [ano, mes, dia] = data.split('-');

  return `${dia}/${mes}/${ano} - ${hora.slice(0, 5)}`;
}

/**
 * O texto da resposta em parágrafos: cada bloco separado por linha em branco vira um, aparado,
 * e os vazios somem. A quebra simples dentro de um bloco fica — a tela a preserva. O e-SIC usa
 * a mesma regra nas respostas dele.
 */
export function emParagrafos(texto: string): string[] {
  return texto
    .replace(/\r\n?/g, '\n')
    .split(/\n\s*\n/)
    .map((paragrafo) => paragrafo.trim())
    .filter((paragrafo) => paragrafo.length > 0);
}

/**
 * Uma resposta do órgão no modelo da tela. A ordem da API (da mais antiga para a mais
 * recente) é mantida aqui, como no `historico`: quem decide mostrar a mais recente primeiro é
 * o detalhe. "Minhas solicitações" converte as respostas com esta mesma função.
 */
export function paraRespostaAcompanhamento(resposta: RespostaPublicaResponse): RespostaSolicitacao {
  return {
    respondidaEm: dataHoraHistorico(resposta.datInicioRss),
    paragrafos: emParagrafos(resposta.desTextoRss),
    status: resposta.desStatusRss,
    links: [...resposta.links],
    formularios: [...resposta.formularios],
    anexos: resposta.anexos.map((anexo) => ({
      codigo: anexo.codAnexoRespostaSolicitacaoArs,
      nome: anexo.desNomeOriginalArs,
      tamanho: tamanhoLegivel(anexo.numTamanhoBytesArs),
    })),
  };
}

/**
 * O aviso local de um download de anexo que falhou. O 404 do back-end é o mesmo para qualquer
 * desencontro (protocolo, órgão, anexo, objeto no bucket), então a frase não especula a causa.
 */
export function mensagemErroDownloadAnexo(erro: unknown): string {
  const status = erro instanceof HttpErrorResponse ? erro.status : 0;

  switch (status) {
    case 404:
      return 'Este arquivo não está mais disponível. Consulte o protocolo novamente.';

    case 429:
      return LIMITE;

    default:
      return 'Não foi possível baixar o arquivo agora. Tente novamente em instantes.';
  }
}

/**
 * A consulta pública convertida no modelo que a tela de acompanhamento já renderiza (que
 * agrupa ouvidoria, SIC e serviço). Função pura: é a ponte entre os dois vocabulários de status.
 */
export function paraSolicitacaoAcompanhamento(resposta: SolicitacaoPublicaResponse): Solicitacao {
  const abertaEm = resposta.datInicioSsv.slice(0, 10);

  return {
    protocolo: resposta.desProtocoloSsv,
    servico: resposta.desNomeServicoSsv,
    // Nulos no agendamento feito pela área do cidadão, que não tem formulário: a tela esconde
    // os dois blocos quando o texto é vazio.
    descricao: resposta.desDescricaoSsv?.trim() ?? '',
    endereco: resposta.desEndereco?.trim() ?? '',
    abertaEm,
    abertaAs: resposta.datInicioSsv.slice(11, 16),
    status: statusParaAcompanhamento(resposta.desStatusSsv),
    categoria: 'servico',
    tipo: resposta.datAgendamentoSsv ? 'Agendamento de serviço' : 'Solicitação de serviço',
    prazo: textoPrazoSolicitacao(resposta),
    segundaVia: resposta.bitSegundaViaSsv,
    pendencia: resposta.pendenciaAberta
      ? paraPendenciaAcompanhamento(resposta.pendenciaAberta, resposta.desTipoContagemSsv)
      : undefined,
    arquivos: resposta.anexos.map((anexo, indice) => ({
      id: `anexo-${indice}`,
      nome: anexo.desNomeOriginalAns,
      data: abertaEm,
      tamanho: tamanhoLegivel(anexo.numTamanhoBytesAns),
      url: '',
    })),
    historico: resposta.historico.map(paraEventoAcompanhamento),
    respostas: resposta.respostas.map(paraRespostaAcompanhamento),
  };
}

/**
 * O pedido de complemento no modelo da tela. O limite sai como a previsão: só a data nos tipos
 * de dia, data e hora em `Horas` — por isso pede o tipo de contagem da solicitação.
 */
export function paraPendenciaAcompanhamento(
  pendencia: PendenciaPublicaResponse,
  tipoContagem: string | null,
): PendenciaSolicitacao {
  return {
    mensagem: pendencia.desMensagemPds,
    pedidaEm: dataHoraHistorico(pendencia.datInicioPds),
    limite: pendencia.datLimitePds ? formatarDataPrazo(pendencia.datLimitePds, tipoContagem) : '',
    pausaPrazo: pendencia.bitPausaPrazoPds,
  };
}

/** Um evento do histórico no modelo da tela, na ordem em que a API o devolve. */
export function paraEventoAcompanhamento(
  evento: SolicitacaoPublicaResponse['historico'][number],
  indice: number,
): EventoHistorico {
  return {
    id: `evento-${indice}`,
    rotulo: evento.desStatusNovoEvs,
    tom: tomDoStatusSolicitacao(evento.desStatusNovoEvs),
    quando: dataHoraHistorico(evento.datEventoEvs),
    descricao: evento.desMensagemEvs,
  };
}
